// KidRatePrompt — the "How was it?" banner that surfaces on the
// kid's Pet tab whenever they're a diner on a recent cook with no
// rating yet. Four face buttons; tap = submit a cook_log_reviews
// row keyed to (cook_log, reviewer=kid). On the 🤩 LOVED IT face
// we also auto-add the recipe to kid_favorites AND open a photo
// prompt so the dish gets a picture on its recipe page.
//
// EMOJI-IN-UI EXCEPTION: this tree (src/experiments/kids-mode/**)
// is the explicit opt-in to emojis.

import { useRef, useState } from "react";
import { COLOR, FONT, RADIUS } from "../../lib/tokens";
import { supabase } from "../../lib/supabase";
import { usePendingKidReviews } from "../../lib/usePendingKidReviews";
import { useRecipePhotos } from "../../lib/useRecipePhotos";

// Kid rating scale — softer than the adult one (no "rough one";
// "didn't like" is the floor and it stays neutral, not punishing).
const KID_RATINGS = [
  { value: "nailed", emoji: "🤩", label: "LOVED IT", color: COLOR.gold,  bg: COLOR.goldDeep,  border: COLOR.goldDim },
  { value: "good",   emoji: "😊", label: "LIKED IT", color: COLOR.leaf,  bg: COLOR.leafDeep,  border: COLOR.leafDim },
  { value: "meh",    emoji: "😐", label: "IT WAS OK", color: COLOR.dim,   bg: COLOR.soil,      border: COLOR.border  },
  { value: "rough",  emoji: "😞", label: "DIDN'T LOVE IT", color: COLOR.rose, bg: COLOR.roseDeep, border: COLOR.roseDim },
];

export function KidRatePrompt({ userId, onLoved }) {
  const { pending, refresh } = usePendingKidReviews(userId);
  const [submitting, setSubmitting] = useState(false);
  const [photoCard, setPhotoCard] = useState(null);
  const fileRef = useRef(null);

  // Only show the top-of-stack pending review. Once rated, the
  // hook drops it from the list and the next (if any) appears.
  const head = pending[0] || null;

  // Photo uploads attach to the recipe whose cook just happened.
  const photos = useRecipePhotos(photoCard?.recipe_slug, userId);

  if (!head && !photoCard) return null;

  const submit = async (rating) => {
    if (!head || submitting) return;
    setSubmitting(true);
    const { error } = await supabase.from("cook_log_reviews").upsert({
      cook_log_id: head.id,
      reviewer_id: userId,
      rating,
      notes: null,
    }, { onConflict: "cook_log_id,reviewer_id" });
    setSubmitting(false);
    if (error) {
      console.warn("[kid-rate] submit:", error.message);
      return;
    }

    // Dismiss the prompt NOW — don't wait on the realtime
    // cook_log_reviews INSERT event, which can lag or never arrive for
    // a kid pseudo-user (RLS / publication). refresh() re-queries and
    // drops this just-rated cook from `pending`. Without this the "How
    // was it?" banner stayed up after the kid picked a rating.
    refresh();

    // On LOVED IT → auto-add to favorites + prompt for photo.
    if (rating === "nailed" && head.recipe_slug) {
      await supabase.from("kid_favorites").insert({
        kid_user_id: userId,
        item_kind:   "recipe",
        item_id:     head.recipe_slug,
        item_emoji:  head.recipe_emoji,
        item_name:   head.recipe_title || head.recipe_slug,
      }).then(({ error }) => {
        // Duplicate-key is fine; they had it before, still loved it.
        if (error && !String(error.message || "").includes("duplicate key")) {
          console.warn("[kid-rate] auto-favorite:", error.message);
        }
      });
      setPhotoCard(head);
      onLoved?.(head);
    }
  };

  const takePhoto = () => fileRef.current?.click();
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await photos.uploadFromFile(file, { setAsPrimary: !photos.primary });
    e.target.value = "";
    setPhotoCard(null);
  };

  // Photo prompt — second-stage card shown after LOVED IT lands.
  if (photoCard) {
    return (
      <div
        style={{
          width: "100%",
          maxWidth: 440,
          margin: "0 auto 18px",
          padding: 18,
          background: "#FFF8E7",
          border: `2px solid ${COLOR.gold}`,
          borderRadius: RADIUS.xxl,
          textAlign: "center",
          boxShadow: `0 8px 22px ${COLOR.goldDeep}`,
        }}
      >
        <div style={{ fontSize: 56, lineHeight: 1, marginBottom: 6 }}>📸</div>
        <div
          style={{
            fontFamily: FONT.itemTitle,
            fontStyle: "italic",
            fontSize: 22,
            color: COLOR.ink,
            marginBottom: 14,
            lineHeight: 1.2,
          }}
        >
          Take a picture!
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button
            type="button"
            onClick={() => setPhotoCard(null)}
            style={{
              flex: 1,
              padding: 14,
              background: "transparent",
              color: COLOR.muted,
              border: `1px solid ${COLOR.border}`,
              borderRadius: 12,
              fontFamily: FONT.mono,
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: "0.08em",
              cursor: "pointer",
            }}
          >
            SKIP
          </button>
          <button
            type="button"
            onClick={takePhoto}
            disabled={photos.uploading}
            style={{
              flex: 2,
              padding: 14,
              background: COLOR.gold,
              color: "#fff",
              border: "none",
              borderRadius: 12,
              fontFamily: FONT.mono,
              fontSize: 14,
              fontWeight: 700,
              letterSpacing: "0.08em",
              cursor: photos.uploading ? "wait" : "pointer",
              boxShadow: `0 4px 14px ${COLOR.goldDeep}`,
            }}
          >
            {photos.uploading ? "UPLOADING…" : "📸 OPEN CAMERA"}
          </button>
        </div>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={onFile}
          style={{ display: "none" }}
        />
      </div>
    );
  }

  return (
    <div
      style={{
        width: "100%",
        maxWidth: 440,
        margin: "0 auto 18px",
        padding: 18,
        background: "rgba(255,255,255,0.78)",
        border: `1.5px solid ${COLOR.border}`,
        borderRadius: RADIUS.xxl,
        textAlign: "center",
        boxShadow: "0 4px 16px rgba(30,30,30,0.08)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 10,
          marginBottom: 8,
        }}
      >
        <span style={{ fontSize: 36 }}>{head.recipe_emoji || "🍽️"}</span>
        <div
          style={{
            fontFamily: FONT.itemTitle,
            fontStyle: "italic",
            fontSize: 20,
            color: COLOR.ink,
            textAlign: "left",
            lineHeight: 1.15,
          }}
        >
          How was it?
        </div>
      </div>
      <div
        style={{
          fontFamily: FONT.forma,
          fontSize: 13,
          color: COLOR.muted,
          marginBottom: 14,
        }}
      >
        {head.recipe_title || "your meal"}
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: 8,
        }}
      >
        {KID_RATINGS.map((r) => (
          <button
            key={r.value}
            type="button"
            onClick={() => submit(r.value)}
            disabled={submitting}
            aria-label={r.label.toLowerCase()}
            className="mcm-focusable"
            style={{
              all: "unset",
              cursor: submitting ? "wait" : "pointer",
              padding: "12px 4px 10px",
              background: r.bg,
              border: `2px solid ${r.border}`,
              borderRadius: 14,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 4,
              touchAction: "manipulation",
              opacity: submitting ? 0.5 : 1,
              transition: "opacity 200ms ease",
            }}
          >
            <span style={{ fontSize: 32, lineHeight: 1 }}>{r.emoji}</span>
            <span
              style={{
                fontFamily: FONT.mono,
                fontSize: 9,
                fontWeight: 700,
                letterSpacing: "0.06em",
                color: r.color,
              }}
            >
              {r.label}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

export default KidRatePrompt;
